/**
 * Utilitaires centralisés pour le formatage des dates
 * Évite les problèmes de timezone lors de la conversion des dates
 */

/**
 * Formate une date en chaîne YYYY-MM-DD sans problème de timezone
 * Utilise les composants locaux de la date (getFullYear, getMonth, getDate)
 * au lieu de toISOString() qui convertit en UTC
 * 
 * @param {Date|Object} date - Date à formater (peut être un objet Date ou dayjs)
 * @returns {string} Date formatée en YYYY-MM-DD
 */
export const formatDateSafe = (date) => {
  if (!date) return '';
  
  let dateObj = date;
  
  // Si c'est un objet dayjs, le convertir en Date
  if (date && typeof date === 'object' && date.toDate) {
    dateObj = date.toDate();
  }
  
  // Si ce n'est pas un objet Date valide, retourner une chaîne vide
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    console.warn('formatDateSafe: Date invalide reçue:', date);
    return '';
  }
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Formate une date pour l'affichage utilisateur (DD/MM/YYYY HH:MM)
 * 
 * @param {Date|Object} date - Date à formater
 * @returns {string} Date formatée en DD/MM/YYYY HH:MM
 */
export const formatDateDisplay = (date) => {
  if (!date) return '';
  
  let dateObj = date;
  
  // Si c'est une string ISO, la convertir en Date
  if (typeof date === 'string') {
    dateObj = new Date(date);
  }
  
  // Si c'est un objet dayjs, le convertir en Date
  if (date && typeof date === 'object' && date.toDate) {
    dateObj = date.toDate();
  }
  
  // Si ce n'est pas un objet Date valide, retourner une chaîne vide
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    console.warn('formatDateDisplay: Date invalide reçue:', date);
    return '';
  }
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Crée un objet Date local à partir des composants d'une date
 * Évite les problèmes de timezone en créant la date directement avec les composants
 * 
 * @param {Date|Object} date - Date source
 * @returns {Date} Nouvel objet Date créé avec les composants locaux
 */
export const createLocalDate = (date) => {
  if (!date) return null;
  
  let dateObj = date;
  
  // Si c'est un objet dayjs, extraire les composants
  if (date && typeof date === 'object' && date.toDate) {
    return new Date(date.year(), date.month(), date.date());
  }
  
  // Si c'est un objet Date, créer un nouveau Date local
  if (dateObj instanceof Date && !isNaN(dateObj.getTime())) {
    return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  }
  
  console.warn('createLocalDate: Date invalide reçue:', date);
  return null;
};

/**
 * Convertit une chaîne de date YYYY-MM-DD en objet Date local
 * 
 * @param {string} dateString - Chaîne de date au format YYYY-MM-DD
 * @returns {Date|null} Objet Date ou null si invalide
 */
export const parseLocalDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;
  
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Les mois sont 0-indexés
  const day = parseInt(parts[2], 10);
  
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  
  const date = new Date(year, month, day);
  
  // Vérifier que la date est valide
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return null;
  }
  
  return date;
};

/**
 * Utilitaire de debug pour logger les informations de date
 * 
 * @param {string} context - Contexte du debug
 * @param {Object} dateInfo - Informations sur les dates
 */
export const debugDateFormatting = (context, dateInfo) => {
  console.log(`🐛 DEBUG ${context}:`, {
    ...dateInfo,
    timestamp: new Date().toISOString()
  });
};

export default {
  formatDateSafe,
  formatDateDisplay,
  createLocalDate,
  parseLocalDate,
  debugDateFormatting
};
